import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay, addDays, addWeeks } from 'date-fns';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './BatchSchedule.css';

const DragAndDropCalendar = withDragAndDrop(Calendar);

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Custom 2-week view range
const getTwoWeekRange = (date) => {
  const start = startOfWeek(date, { weekStartsOn: 0 });
  const end = addDays(start, 13); // 2 weeks = 14 days

  let current = start;
  const range = [];
  while (current <= end) {
    range.push(current);
    current = addDays(current, 1);
  }
  return range;
};

function BatchSchedule() {
  const [batches, setBatches] = useState([]);
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newDate, setNewDate] = useState('');

  // Fetch batches with their blends
  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/batches');
      setBatches(response.data);

      console.log('Total batches fetched:', response.data.length);

      // Convert batches to calendar events based on their blend dates
      const calendarEvents = [];
      let batchesWithoutDates = [];

      for (const batch of response.data) {
        // Fetch blends for this batch
        try {
          const blendsResponse = await axios.get(`/api/batches/${batch.id}/blends`);
          const blends = blendsResponse.data;

          console.log(`Batch ${batch.batch_name}: ${blends.length} blends`);

          // Create an event for each blend
          blends.forEach(blend => {
            if (blend.blend_date) {
              const startDate = new Date(blend.blend_date + 'T00:00:00');
              const endDate = new Date(startDate);
              endDate.setDate(endDate.getDate() + 2); // +2 days

              const event = {
                id: `${batch.id}-${blend.id}`,
                title: `${batch.batch_name} - ${batch.fragrance_oil_name}`,
                start: startDate,
                end: endDate,
                resource: { ...batch, blend },
                allDay: true,
              };

              calendarEvents.push(event);
              console.log(`Added blend event:`, event);
            } else {
              console.log(`Blend ${blend.blend_name || blend.id} has no blend_date`);
            }
          });

          // If batch has no blends but has scheduled_start_date, show it
          if (blends.length === 0 && batch.scheduled_start_date) {
            const startDate = new Date(batch.scheduled_start_date + 'T00:00:00');
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 2);

            calendarEvents.push({
              id: batch.id,
              title: `${batch.batch_name} - ${batch.fragrance_oil_name}`,
              start: startDate,
              end: endDate,
              resource: batch,
              allDay: true,
            });
            console.log(`Added batch event: ${batch.batch_name} on ${batch.scheduled_start_date}`);
          } else if (blends.length === 0 && !batch.scheduled_start_date) {
            batchesWithoutDates.push(batch.batch_name);
          }
        } catch (blendError) {
          console.error(`Error fetching blends for batch ${batch.id}:`, blendError);
          // If blend fetch fails, fall back to scheduled_start_date
          if (batch.scheduled_start_date) {
            const startDate = new Date(batch.scheduled_start_date + 'T00:00:00');
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 2);

            calendarEvents.push({
              id: batch.id,
              title: `${batch.batch_name} - ${batch.fragrance_oil_name}`,
              start: startDate,
              end: endDate,
              resource: batch,
              allDay: true,
            });
          }
        }
      }

      console.log('Total calendar events:', calendarEvents.length);
      if (batchesWithoutDates.length > 0) {
        console.log('Batches without dates (not shown on calendar):', batchesWithoutDates.join(', '));
      }

      setEvents(calendarEvents);
      setError(null);
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  // Handle event click
  const handleSelectEvent = useCallback((event) => {
    setSelectedBatch(event.resource);
    // Get the date from blend if available, otherwise from scheduled_start_date
    const currentDate = event.resource.blend?.blend_date || event.resource.scheduled_start_date || '';
    setNewDate(currentDate);
    setShowModal(true);
  }, []);

  // Handle date update
  const handleUpdateDate = async () => {
    if (!selectedBatch || !newDate) return;

    try {
      // If there's a blend, update the blend date
      if (selectedBatch.blend) {
        await axios.patch(`/api/blends/${selectedBatch.blend.id}`, {
          blend_date: newDate,
        });
      } else {
        // Otherwise update the batch scheduled_start_date
        await axios.patch(`/api/batches/${selectedBatch.id}/schedule-date`, {
          scheduled_start_date: newDate,
        });
      }

      setShowModal(false);
      setSelectedBatch(null);
      setNewDate('');
      await fetchBatches();
    } catch (err) {
      console.error('Error updating date:', err);
      alert('Failed to update date');
    }
  };

  // Handle event drag and drop
  const handleEventDrop = async ({ event, start, end }) => {
    try {
      const newStartDate = format(start, 'yyyy-MM-dd');
      const batch = event.resource;

      // If there's a blend, update the blend date
      if (batch.blend) {
        await axios.patch(`/api/blends/${batch.blend.id}`, {
          blend_date: newStartDate,
        });
      } else {
        // Otherwise update the batch scheduled_start_date
        await axios.patch(`/api/batches/${batch.id}/schedule-date`, {
          scheduled_start_date: newStartDate,
        });
      }

      // Refresh calendar
      await fetchBatches();
    } catch (err) {
      console.error('Error updating date via drag:', err);
      alert('Failed to update date');
      // Refresh to revert changes
      await fetchBatches();
    }
  };

  // Handle slot selection (clicking on calendar to create/schedule)
  const handleSelectSlot = useCallback((slotInfo) => {
    // Find batches without scheduled dates
    const unscheduledBatches = batches.filter(b => !b.scheduled_start_date);

    if (unscheduledBatches.length === 0) {
      alert('All batches have scheduled dates. Click on a batch to reschedule it.');
      return;
    }

    // For simplicity, just show available batches
    const batchNames = unscheduledBatches.map(b => b.batch_name).join(', ');
    alert(`Unscheduled batches: ${batchNames}\n\nClick on a batch in the Batch List tab to edit its details and set a schedule date.`);
  }, [batches]);

  // Custom event style
  const eventStyleGetter = (event) => {
    const batch = event.resource;
    let backgroundColor = '#999';

    switch (batch.status) {
      case 'draft':
        backgroundColor = '#999';
        break;
      case 'in_production':
        backgroundColor = '#ff9800';
        break;
      case 'completed':
        backgroundColor = '#4caf50';
        break;
      default:
        backgroundColor = '#666';
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  if (loading) {
    return (
      <div className="batch-schedule-loading">
        <div className="spinner">
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="batch-schedule-error">
        <p>{error}</p>
        <button onClick={fetchBatches}>Retry</button>
      </div>
    );
  }

  return (
    <div className="batch-schedule">
      <div className="schedule-header">
        <h2>Batch Production Schedule</h2>
        <div className="schedule-legend">
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#999' }}></span>
            <span>Draft</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ff9800' }}></span>
            <span>In Production</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#4caf50' }}></span>
            <span>Completed</span>
          </div>
        </div>
        <button onClick={fetchBatches} className="refresh-schedule-btn">
          Refresh
        </button>
      </div>

      <div className="schedule-info">
        <p>Drag batches to reschedule, or click to edit details. Batches span 2 days from blend start to completion. Use Week view for best scheduling visibility.</p>
        <p>Total scheduled blends: <strong>{events.length}</strong></p>
        {events.length === 0 && !loading && (
          <p style={{ color: '#ff9800', fontWeight: 'bold', marginTop: '10px' }}>
            No batches showing? Create a blend for your batch and set a blend date in the Batch List tab.
          </p>
        )}
      </div>

      <div className="calendar-container">
        <DragAndDropCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 700 }}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          onEventDrop={handleEventDrop}
          selectable
          eventPropGetter={eventStyleGetter}
          views={['month', 'week', 'agenda']}
          defaultView="week"
          date={currentDate}
          onNavigate={(date) => setCurrentDate(date)}
          toolbar={true}
          showMultiDayTimes={false}
          draggableAccessor={() => true}
          resizable={false}
        />
      </div>

      {/* Edit Date Modal */}
      {showModal && selectedBatch && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              {selectedBatch.blend ? 'Schedule Blend' : 'Schedule Batch'}: {selectedBatch.batch_name}
              {selectedBatch.blend && ` - ${selectedBatch.blend.blend_name}`}
            </h3>

            <div className="batch-details">
              <p><strong>Fragrance:</strong> {selectedBatch.fragrance_oil_name}</p>
              <p><strong>Status:</strong> {selectedBatch.status}</p>
              <p><strong>Total Units:</strong> {selectedBatch.total_units}</p>
              <p><strong>Work Orders:</strong> {selectedBatch.batch_work_orders?.length || 0}</p>
              {selectedBatch.blend && (
                <>
                  <p><strong>Blend:</strong> {selectedBatch.blend.blend_name}</p>
                  <p><strong>Melter:</strong> {selectedBatch.blend.melter_name || 'N/A'}</p>
                </>
              )}
            </div>

            <div className="date-input-section">
              <label htmlFor="schedule-date">
                <strong>{selectedBatch.blend ? 'Blend Date:' : 'Production Start Date:'}</strong>
              </label>
              <input
                id="schedule-date"
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="date-input"
              />
              <p className="date-note">
                {selectedBatch.blend
                  ? 'This will update the blend date and work order schedules'
                  : `Completion date will be set to ${newDate ? format(addDays(new Date(newDate + 'T00:00:00'), 2), 'MMM dd, yyyy') : 'N/A'}`
                }
              </p>
            </div>

            <div className="modal-actions">
              <button onClick={handleUpdateDate} className="save-btn">
                Update Schedule
              </button>
              <button onClick={() => setShowModal(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BatchSchedule;
